<template>
  <section class="container-fluid px-0">
    <div class="px-0">
      <div class="panel-toolbar">
        <router-link to="/panel/pedidos" class="btn btn-outline-primary">
          <i class="fas fa-arrow-left me-2"></i>
          Volver a pedidos
        </router-link>
      </div>

      <div v-if="isLoading" class="row g-4">
        <div class="col-12">
          <div class="skeleton-loader">
            <div class="skeleton-text w-50"></div>
            <div class="skeleton-text w-75"></div>
            <div class="skeleton-text w-75"></div>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
        <button
          @click="fetchOrderDetails"
          class="btn btn-outline-danger btn-sm ms-3"
        >
          <i class="fas fa-sync-alt me-1"></i>Reintentar
        </button>
      </div>

      <div v-else class="row g-4">
        <div class="col-12">
          <div class="card bg-dark text-light">
            <div class="card-body">
              <div class="order-header">
                <div>
                  <h4 class="mb-1 fw-semibold">Pedido #{{ order.id }}</h4>
                  <div class="text-muted small">Creado: {{ parseTimestamp(order.created_at) }}</div>
                </div>
                <div class="order-header-actions">
                  <span class="status-pill" :class="statusClass">{{ order.status_display }}</span>
                </div>
              </div>

              <div class="order-metrics">
                <div class="metric-card">
                  <div class="metric-label">Total</div>
                  <div class="metric-value">${{ formatPriceString(order.total) }}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">Pago</div>
                  <div class="metric-value-sm">
                    {{ order.payment_method_display || paymentMethodLabel(order.payment_method) }}
                  </div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">Entrega</div>
                  <div class="metric-value-sm">
                    {{ order.delivery_type_display || order.delivery_type }}
                  </div>
                </div>
                <div class="metric-card" v-if="order.shipping_service_name || order.delivery_type === 'pickup'">
                  <div class="metric-label">Envio</div>
                  <div class="metric-value-sm">
                    {{ order.delivery_type === 'pickup' ? 'Acordar envío · sin cargo en este pedido' : order.shipping_service_name }}
                    <span v-if="order.shipping_cost > 0" class="text-muted">
                      ({{ formatPriceString(order.shipping_cost) }})
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="order.delivery_type === 'pickup' && ['shipped', 'completed'].includes(order.status)" class="mt-3">
                <button class="btn btn-outline-primary" :disabled="sendingTrackingNotification || order.shipping_notification_sent" @click="sendArrangedShippingNotification">
                  {{ order.shipping_notification_sent ? 'Aviso de envío enviado' : 'Notificar envío al cliente' }}
                </button>
              </div>
              <div v-if="order.shipment && order.delivery_type === 'delivery'" class="section-card tracking-section mt-3">
                <div class="section-title mb-2">
                  <i class="fas fa-barcode me-2 text-info"></i>
                  Seguimiento del envío
                </div>
                <div v-if="checkingTracking" class="text-muted small">
                  <i class="fas fa-spinner fa-spin me-1"></i>Verificando el código en MiCorreo…
                </div>
                <template v-else-if="order.shipment.tracking_number">
                  <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                      <div class="text-muted small mb-1">Código de seguimiento</div>
                      <div class="tracking-code-detail">{{ order.shipment.tracking_number }}</div>
                    </div>
                    <div class="d-flex flex-wrap gap-2">
                      <button type="button" class="btn btn-outline-secondary" :disabled="savingTrackingEdit" @click="startTrackingEdit">
                        <i class="fas fa-pen me-1"></i>Editar código
                      </button>
                      <button
                        ref="trackingNotifyButton"
                        type="button"
                        class="btn btn-outline-info"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        :title="trackingNotificationTooltip"
                        :disabled="sendingTrackingNotification || savingTrackingEdit || (editingTracking && trackingEditValue.trim() !== order.shipment.tracking_number)"
                        @click="sendTrackingNotification"
                      >
                        <span v-if="sendingTrackingNotification">
                          <i class="fas fa-spinner fa-spin me-1"></i>Enviando…
                        </span>
                        <span v-else>
                          <i class="fas fa-envelope me-1"></i>
                          {{ order.shipment.tracking_notification_sent ? 'Reenviar aviso' : 'Avisar al cliente' }}
                        </span>
                      </button>
                    </div>
                  </div>
                </template>
                <div v-else class="text-muted small">
                  <div class="mb-2">
                    <i class="fas fa-info-circle me-1"></i>Este pedido todavía no tiene un código de seguimiento registrado.
                  </div>
                  <div v-if="order.shipment.shipping_id">
                    <i class="fas fa-clock me-1"></i>MiCorreo todavía no devolvió el código de seguimiento.
                  </div>
                  <button type="button" class="btn btn-sm btn-outline-secondary mt-2" @click="startTrackingEdit">
                    <i class="fas fa-plus me-1"></i>Agregar código
                  </button>
                </div>
                <div v-if="editingTracking" class="mt-3">
                  <label for="trackingEditDetail" class="form-label">Código de seguimiento</label>
                  <input id="trackingEditDetail" v-model="trackingEditValue" type="text" maxlength="100" class="form-control" @keyup.enter="saveTrackingEdit">
                  <p v-if="order.shipment.tracking_notification_sent" class="small text-warning mt-2 mb-0">
                    El código anterior ya se envió. Después de guardar, reenviá el aviso al cliente.
                  </p>
                  <div class="d-flex gap-2 mt-2">
                    <button type="button" class="btn btn-primary btn-sm" :disabled="savingTrackingEdit || !trackingEditValue.trim()" @click="saveTrackingEdit">
                      {{ savingTrackingEdit ? 'Guardando…' : 'Guardar código' }}
                    </button>
                    <button type="button" class="btn btn-secondary btn-sm" :disabled="savingTrackingEdit" @click="editingTracking = false">Cancelar</button>
                  </div>
                </div>
              </div>

              <div class="row g-4 mt-2">
                <!-- Información del cliente -->
                <div class="col-md-5">
                  <div class="section-card">
                    <div class="section-title">
                      <i class="fas fa-user me-2 text-primary"></i>
                      Información del Cliente
                    </div>
                    <div class="customer-info-list">
                      <p class="info-row">
                        <span class="info-label">Nombre</span>
                        <span class="info-value">{{ order.recipient_name }}</span>
                      </p>
                      <p class="info-row">
                        <span class="info-label">Telefono</span>
                        <span class="info-value">{{ order.customer_phone }}</span>
                      </p>
                      <p class="info-row">
                        <span class="info-label">Email</span>
                        <span class="info-value">{{ order.customer_email }}</span>
                      </p>
                      <p class="info-row">
                        <span class="info-label">Direccion</span>
                        <span class="info-value">{{ order.shipping_address }}</span>
                      </p>
                    </div>
                    <button v-if="order.customer_email" type="button" class="btn btn-outline-primary btn-sm mt-3" @click="contactModal?.openForOrder(order)">
                      <i class="fas fa-envelope me-1"></i>Contactar al cliente
                    </button>
                  </div>
                </div>
                
                <!-- Productos -->
                <div class="col-md-7">
                  <div class="section-card">
                    <div class="section-title">
                      <i class="fas fa-box me-2 text-warning"></i>
                      Productos
                    </div>
                    
                    <div v-if="!orderProducts || orderProducts.length === 0" class="alert alert-warning mb-0">
                      No se encontraron productos en este pedido.
                    </div>
                    
                    <div v-else class="product-list">
                      <div v-for="product in orderProducts" :key="product.id" class="product-item">
                        <div class="d-flex align-items-center">
                          <div class="order-product-image">
                            <img
                              :src="product.product_image || '/logo.png'"
                              :alt="product.product_name || 'Producto'"
                              class="img-thumbnail"
                            />
                          </div>
                          <div class="product-details ms-3">
                            <div class="d-flex justify-content-between align-items-center">
                              <h6 class="product-name mb-0">{{ product.product_name }}</h6>
                              <div class="product-price">${{ formatPriceString(product.price_at_time) }}</div>
                            </div>
                            <div class="product-quantity text-muted">
                              Cantidad: {{ product.quantity }}
                            </div>
                            <div v-if="product.blister_info" class="blister-info mt-1 text-muted">
                              <small>{{ product.blister_info.seed_count }} semillas</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="section-card mt-3">
                    <div class="section-title">
                      <i class="fas fa-receipt me-2 text-white"></i>
                      Resumen
                    </div>
                    <div class="order-total">
                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-muted">Subtotal</span>
                        <span>${{ formatPriceString(order.subtotal) }}</span>
                      </div>
                      <div v-if="order.discount > 0" class="d-flex justify-content-between mb-2">
                        <span class="text-muted">Descuento</span>
                        <span>- ${{ formatPriceString(order.discount) }}</span>
                      </div>
                      <div v-if="order.shipping_cost > 0" class="d-flex justify-content-between mb-2">
                        <span class="text-muted">Envio</span>
                        <span>${{ formatPriceString(order.shipping_cost) }}</span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center border-top border-secondary pt-2">
                        <h5 class="mb-0">Total:</h5>
                        <div class="total-price">${{ formatPriceString(order.total) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notas del pedido -->
              <div class="order-notes-section mt-4">
                <div class="section-title">
                  <i class="fas fa-sticky-note me-2 text-warning"></i>Notas del pedido
                </div>
                <p class="text-muted small mb-2">Notas internas — solo visible para administradores.</p>
                <textarea
                  v-model="orderNotes"
                  class="form-control notes-control"
                  rows="3"
                  placeholder="Agregar notas sobre este pedido…"
                ></textarea>
                <div class="d-flex justify-content-end mt-2">
                  <button
                    class="btn btn-sm btn-outline-warning"
                    :disabled="savingNotes"
                    @click="saveOrderNotes"
                  >
                    <i class="fas fa-save me-1"></i>
                    <span v-if="savingNotes">Guardando…</span>
                    <span v-else>Guardar nota</span>
                  </button>
                </div>
              </div>

              <!-- Botones de acción -->
              <div class="mt-4 d-flex justify-content-end gap-2">
                <button class="btn btn-secondary" @click="editOrder">
                  <i class="fas fa-edit me-1"></i>
                  Editar Pedido
                </button>
                <button class="btn btn-danger" @click="deleteOrder">
                  <i class="fas fa-trash me-1"></i>
                  Eliminar Pedido
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Edit Order Modal -->
  <div
    class="modal fade"
    id="editOrderModal"
    tabindex="-1"
    aria-labelledby="editOrderModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content bg-dark text-light">
        <div class="modal-header">
          <h5 class="modal-title" id="editOrderModalLabel">Editar Pedido</h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedOrder">
            <div class="mb-3">
              <label for="customerName" class="form-label"
                >Nombre del Cliente</label
              >
              <input
                type="text"
                class="form-control"
                id="customerName"
                v-model="selectedOrder.customer_name"
              />
            </div>
            <div class="mb-3">
              <label for="customerPhone" class="form-label"
                >Teléfono del Cliente</label
              >
              <input
                type="text"
                class="form-control"
                id="customerPhone"
                v-model="selectedOrder.customer_phone"
              />
            </div>
            <div class="mb-3">
              <label for="orderStatus" class="form-label">Estado</label>
              <select
                class="form-control"
                id="orderStatus"
                v-model="selectedOrder.status"
              >
                <option value="pending">Pendiente</option>
                <option value="completed">Completada</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
          </div>
          <div v-else>
            <p>No se encontraron detalles del pedido.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Cerrar
          </button>
          <button type="button" class="btn btn-primary" @click="updateOrder">
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  </div>
  <CustomerContactModal ref="contactModal" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from "vue";
import {
  getOrder,
  updateOrder as updateOrderAPI,
  deleteOrder as deleteOrderAPI,
} from "@/services/orders";
import { getProduct } from "@/services/products";
import { parseTimestamp } from "@/utils/dates";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import apiClient from "@/services/apiClient";
import CustomerContactModal from "@/components/admin/CustomerContactModal.vue";
import { getShipmentTracking, notifyShipmentTracking, updateShipment } from "@/services/shipping";
import { useUnsavedChangesGuard } from "@/composables/useUnsavedChangesGuard";

const toast = useToast();
const route = useRoute();
const router = useRouter();
const orderId = route.params.orderId;
const orderProducts = ref([]);

const order = ref(null);
const contactModal = ref(null);
const isLoading = ref(true);
const error = ref(null);
const selectedOrder = ref(null);
const orderNotes = ref('');
const savingNotes = ref(false);
const checkingTracking = ref(false);
const sendingTrackingNotification = ref(false);
const editingTracking = ref(false);
const trackingEditValue = ref('');
const savingTrackingEdit = ref(false);
const trackingNotifyButton = ref(null);
const bootstrap = window.bootstrap;
let trackingTooltip = null;

const formatTrackingDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const trackingNotificationTooltip = computed(() => {
  const shipment = order.value?.shipment;
  if (!shipment?.tracking_notification_sent) return 'Enviar el código de seguimiento al cliente';
  if (shipment.tracking_notification_sent_at) {
    return `Último envío: ${formatTrackingDate(shipment.tracking_notification_sent_at)}`;
  }
  return 'El código ya fue enviado anteriormente';
});

const initTrackingTooltip = async () => {
  await nextTick();
  trackingTooltip?.dispose();
  trackingTooltip = trackingNotifyButton.value
    ? new bootstrap.Tooltip(trackingNotifyButton.value)
    : null;
};

// Cambios sin guardar: nota del pedido o campos del modal "Editar Pedido".
const orderDirty = computed(() => {
  if (!order.value) return false;
  if (orderNotes.value !== (order.value.notes || '')) return true;
  if (selectedOrder.value) {
    return (
      selectedOrder.value.customer_name !== order.value.customer_name ||
      selectedOrder.value.customer_phone !== order.value.customer_phone ||
      selectedOrder.value.status !== order.value.status
    );
  }
  return false;
});

const savePendingChanges = async () => {
  const modalOpen = !!selectedOrder.value;
  const notesDirty = !!order.value && orderNotes.value !== (order.value.notes || '');
  if (modalOpen) await updateOrder();
  if (notesDirty) await saveOrderNotes();
};

useUnsavedChangesGuard({
  dirty: orderDirty,
  saveFn: savePendingChanges,
  message: 'Tenés cambios sin guardar en el pedido (nota o datos del pedido). Si salís ahora, se van a perder.',
});

const fetchOrderDetails = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await getOrder(orderId);
    order.value = response;
    orderNotes.value = response.notes || '';

    const shipment = order.value?.shipment;
    if (shipment?.is_imported && shipment.shipping_id && !shipment.tracking_number) {
      checkingTracking.value = true;
      try {
        const tracking = await getShipmentTracking(shipment.id);
        if (tracking?.shipment) order.value.shipment = tracking.shipment;
      } catch (trackingError) {
        console.warn('No se pudo sincronizar el tracking del pedido:', trackingError);
      } finally {
        checkingTracking.value = false;
      }
    }
    await initTrackingTooltip();
    
    // Usar directamente order_products de la respuesta
    if (order.value && order.value.order_products && order.value.order_products.length > 0) {
      console.log("Productos encontrados:", order.value.order_products);
      orderProducts.value = order.value.order_products;
    } else if (order.value && order.value.products) {
      // Fallback al método anterior si existe products
      orderProducts.value = await Promise.all(
        order.value.products.map(async (productId) => {
          const product = await getProduct(productId);
          return product;
        })
      );
    } else {
      // Si no hay productos, inicializar como array vacío
      orderProducts.value = [];
      console.warn("No se encontraron productos en el pedido");
    }
  } catch (err) {
    error.value = err.message;
    console.error("Error fetching order details:", err);
    toast.error("Error al cargar los detalles del pedido.");
  } finally {
    isLoading.value = false;
  }
};

const sendArrangedShippingNotification = async () => {
  sendingTrackingNotification.value = true;
  try {
    const { data } = await apiClient.post(`/orders/${order.value.id}/notify_shipping/`);
    order.value = data.order;
    toast.success('Aviso de envío enviado.');
  } catch (err) {
    toast.error(err.response?.data?.error || 'No se pudo enviar el aviso de envío.');
  } finally {
    sendingTrackingNotification.value = false;
  }
};

const sendTrackingNotification = async () => {
  const shipment = order.value?.shipment;
  if (!shipment?.id || !shipment.tracking_number) return;
  sendingTrackingNotification.value = true;
  try {
    const result = await notifyShipmentTracking(shipment.id);
    if (result?.shipment) order.value.shipment = result.shipment;
    toast.success(result.status || 'Código de seguimiento enviado correctamente.');
    await initTrackingTooltip();
  } catch (err) {
    toast.error(
      err.response?.data?.error || err.response?.data?.detail ||
      'No se pudo enviar el código de seguimiento.'
    );
  } finally {
    sendingTrackingNotification.value = false;
  }
};

const startTrackingEdit = () => {
  trackingEditValue.value = order.value?.shipment?.tracking_number || '';
  editingTracking.value = true;
};

const saveTrackingEdit = async () => {
  const shipment = order.value?.shipment;
  const code = trackingEditValue.value.trim();
  if (!shipment?.id || !code || savingTrackingEdit.value) return;
  savingTrackingEdit.value = true;
  try {
    order.value.shipment = await updateShipment(shipment.id, { tracking_number: code });
    editingTracking.value = false;
    toast.success(shipment.tracking_notification_sent
      ? 'Código corregido. Reenviá el aviso al cliente.'
      : 'Código de seguimiento guardado.');
    await initTrackingTooltip();
  } catch (err) {
    toast.error(err.response?.data?.error || err.response?.data?.detail || 'No se pudo guardar el código de seguimiento.');
  } finally {
    savingTrackingEdit.value = false;
  }
};

const editOrder = () => {
  selectedOrder.value = { ...order.value };
  const editOrderModal = new bootstrap.Modal(
    document.getElementById("editOrderModal")
  );
  editOrderModal.show();
};

const updateOrder = async () => {
  try {
    await updateOrderAPI(selectedOrder.value.id, selectedOrder.value);
    await fetchOrderDetails();
    const editOrderModal = bootstrap.Modal.getInstance(
      document.getElementById("editOrderModal")
    );
    editOrderModal.hide();
    toast.success("Pedido actualizado correctamente.");
  } catch (err) {
    console.error("Error updating order:", err);
    toast.error("Error al actualizar el pedido.");
  }
};

const saveOrderNotes = async () => {
  if (!order.value) return;
  savingNotes.value = true;
  try {
    await apiClient.patch(`/orders/${order.value.id}/`, { notes: orderNotes.value });
    if (order.value) order.value.notes = orderNotes.value;
    toast.success('Nota guardada');
  } catch (err) {
    toast.error('Error al guardar la nota');
  } finally {
    savingNotes.value = false;
  }
};

const deleteOrder = async () => {
  try {
    await deleteOrderAPI(order.value.id);
    router.push("/panel/pedidos");
    toast.success("Pedido eliminado correctamente.");
  } catch (err) {
    console.error("Error deleting order:", err);
    toast.error("Error al eliminar el pedido.");
  }
};


// Nueva función para manejar precios como string
const paymentMethodLabel = (method) => ({
  online: 'Mercado Pago',
  cash: 'Efectivo / coordinar',
}[method] || method || '—')

const formatPriceString = (price) => {
  if (price === undefined || price === null) {
    return "0";
  }
  
  // Convertir a número si es string
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // Verificar si es un número válido después de la conversión
  if (isNaN(numericPrice)) {
    return "0";
  }
  
  return numericPrice.toLocaleString("es-CL");
};

const statusClass = computed(() => {
  if (!order.value) return {};
  
  return {
    "badge bg-warning": order.value.status === "pending",
    "badge bg-success": order.value.status === "completed",
    "badge bg-danger": order.value.status === "cancelled",
  };
});

// Lifecycle
onMounted(() => {
  fetchOrderDetails();
  // Al cerrar el modal de edición (X, Cancelar o post-guardado) se descartan
  // los cambios del mismo y se apaga el flag de dirty correspondiente.
  const editOrderModalEl = document.getElementById("editOrderModal");
  if (editOrderModalEl) {
    editOrderModalEl.addEventListener("hidden.bs.modal", () => {
      selectedOrder.value = null;
    });
  }
});

onBeforeUnmount(() => {
  trackingTooltip?.dispose();
});
</script>

<style scoped>
.card {
  border: 1px solid var(--panel-border);
  border-radius: 0.5rem;
  background: var(--panel-surface);
  box-shadow: none;
  transition: transform 0.3s ease-in-out;
}

.card:hover {
  transform: translateY(-0.25rem);
}

.skeleton-loader {
  background: #1a1a1a;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.skeleton-text {
  height: 20px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.order-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-pill {
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.85rem;
}

.order-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.metric-card {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 0.9rem 1rem;
}

.metric-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 0.35rem;
}

.metric-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--white-color, #fff);
}

.metric-value-sm {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--white-color, #fff);
}

.section-card {
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1rem;
}

.tracking-section {
  border-color: rgba(13, 202, 240, 0.25);
}

.tracking-code-detail {
  color: #8de7f7;
  font-family: monospace;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.tracking-code-editor {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.tracking-code-editor .form-control {
  flex: 1 1 260px;
}

.section-title {
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.customer-info-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.info-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.55);
}

.info-value {
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--white-color, #fff);
  word-break: break-word;
}

.product-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 10px;
}

.product-item {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 12px 14px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.product-item:hover {
  background: rgba(0, 0, 0, 0.3);
}

.order-product-image {
  width: 80px;
  height: 80px;
  min-width: 80px;
  overflow: hidden;
  border-radius: 6px;
}

.order-product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-name {
  font-weight: 600;
  color: var(--white-color);
}

.product-price {
  font-weight: 600;
  color: var(--primary-color);
}

.customer-info p {
  margin-bottom: 1rem;
}

.total-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--white-color, #fff);
}

.order-total {
  background: rgba(0, 0, 0, 0.2);
  padding: 15px;
  border-radius: 8px;
}

.order-notes-section {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 1.25rem;
}

.notes-control {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--white-color, #fff);
  border-radius: 10px;
  font-size: 0.9rem;
  resize: vertical;
}

.notes-control:focus {
  background: rgba(0, 0, 0, 0.4);
  border-color: #ffc107;
  color: var(--white-color, #fff);
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.15);
}

.notes-control::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 767px) {
  .order-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .order-metrics {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }

  .product-list {
    max-height: 300px;
  }
  
  .order-product-image {
    width: 60px;
    height: 60px;
    min-width: 60px;
  }
}
</style>
